import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";
import { useLazyProfileQuery } from "../../../../redux/api/user/userAPI";
import {
  selectAuth,
  setIsNewUser,
} from "../../../../redux/features/auth/auth-slice";
import { selectProfile } from "../../../../redux/features/user/user-slice";
import { UserRole } from "../../enums/user-roles";
import { AppRoute } from "../../interfaces/app-routes";
import { IHttpError } from "../../interfaces/http-errror";
import SpinnerModal from "../../ui-elements/spinner/spinner";
import isEmailVerified from "../../utils/isEmailVerified";

interface IProps {
  role: UserRole;
}

const PrivateRoute = (props: IProps) => {
  const navigate = useNavigate();
  const auth = useSelector(selectAuth);
  const dispatch = useDispatch();
  const [getProfile, { isFetching, isLoading }] = useLazyProfileQuery();
  const profile = useSelector(selectProfile);

  useEffect(() => {
    getProfile({ role: auth.userRole })
      .unwrap()
      .catch((error: IHttpError) => {
        if (isEmailVerified(error)) {
          if (auth.isNewUser) {
            const email = auth.email;
            dispatch(setIsNewUser({ isNewUser: false, email: "" }));
            navigate(AppRoute.USER_EMAIL_VERIFICATION, {
              state: {
                email: email,
                whereTo: "estimations",
              },
            });
          } else {
            navigate(AppRoute.VERIFY_EMAIL);
          }
        }
      });
  }, []);

  useEffect(() => {
    if (!auth.token) {
      navigate(AppRoute.PUBLIC_DESIGN_SYSTEM);
      return;
    }

    if (isLoading || isFetching) {
      return;
    }

    if (profile && profile.role !== props.role) {
      if (
        profile.role === UserRole.ROLE_USER ||
        profile.role === UserRole.ROLE_SOLAR_DASHBOARD_ADMIN
      ) {
        navigate(AppRoute.DASHBOARD);
      } else {
        navigate(AppRoute.UNAUTHORIZED);
      }
      return;
    }
  }, [auth.token, profile, isLoading, props.role, isFetching]);

  return (
    <>
      <SpinnerModal show={isFetching || isLoading} />
      {!isFetching && !isLoading && <Outlet />}
    </>
  );
};

export default PrivateRoute;
